export interface ResumeFragmentGuard {
  slideIndex: number;
  fragmentFloor: number;
}

interface FragmentGuardAction {
  tutor_action?: {
    fragment_ids?: unknown;
    [key: string]: unknown;
  };
  [key: string]: unknown;
}

export interface FragmentGuardResult<
  TAction extends FragmentGuardAction = FragmentGuardAction,
> {
  action: TAction | null;
  guard: ResumeFragmentGuard;
  originalFragmentIds: number[];
  filteredFragmentIds: number[];
  dropped: boolean;
  advanced: boolean;
}

const isFiniteNumber = (value: unknown): value is number =>
  typeof value === "number" && Number.isFinite(value);

export function applyResumeFragmentGuard<TAction extends FragmentGuardAction>(
  action: TAction | null | undefined,
  guard: ResumeFragmentGuard | null,
  liveSlideIndex: unknown,
): FragmentGuardResult<TAction> | null {
  const tutorAction = action?.tutor_action;
  if (
    !action ||
    !guard ||
    Number(guard.slideIndex) !== Number(liveSlideIndex) ||
    !Array.isArray(tutorAction?.fragment_ids)
  ) {
    return null;
  }

  const originalFragmentIds = tutorAction.fragment_ids.filter(isFiniteNumber);
  if (originalFragmentIds.length === 0) {
    return null;
  }

  const floor = Number(guard.fragmentFloor);
  const filteredFragmentIds = originalFragmentIds.filter((id) => id >= floor);
  if (filteredFragmentIds.length === 0) {
    return {
      action: null,
      guard,
      originalFragmentIds,
      filteredFragmentIds,
      dropped: true,
      advanced: false,
    };
  }

  const nextFloor = Math.max(floor, Math.max(...filteredFragmentIds));
  const nextGuard =
    nextFloor === floor ? guard : { ...guard, fragmentFloor: nextFloor };

  return {
    action: {
      ...action,
      tutor_action: {
        ...tutorAction,
        fragment_ids: filteredFragmentIds,
      },
    } as TAction,
    guard: nextGuard,
    originalFragmentIds,
    filteredFragmentIds,
    dropped: false,
    advanced: nextFloor !== floor,
  };
}
